import type { ReactNode } from "react"
import { useMemo, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../../access/use-access"
import { archiveEmployee, createTimeOff, fetchEmployees, reassignDay } from "../../../api/directory.api"
import { fetchJournal } from "../../../api/journal.api"
import type { ApiProblem } from "../../../api/problem"
import { isApiProblem, transportProblem } from "../../../api/problem"
import { cx } from "../../../lib/cx"
import { formatTimeOfDay, todayInZone } from "../../../lib/format"
import { appointmentsInstrumental } from "../../../lib/plural"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Button, Chip, Chips, Skeleton } from "../../../ui"
import styles from "../directories.module.css"
import { CenterState, StandHeader } from "../stand"
import { useResource } from "../use-resource"
import type { LeaveAction, LeaveNotify, LeavePeriodKind } from "./leave-model"
import {
  NOTIFY_TEMPLATE,
  buildLeavePreview,
  buildLeaveSummary,
  missingServiceNames,
  resolvePeriod,
} from "./leave-model"

/**
 * «Мастер выбывает» (2-2.10.4) — один сценарий на «заболела» и «уволилась».
 *
 * ЭТО ЭКРАН СО СВОИМ АДРЕСОМ /staff/:employeeId/leave, А НЕ МОДАЛЬНОЕ ОКНО, хотя макет
 * рисует его окном поверх списка. Причина в ТЗ §4 и в самом сценарии: здесь четыре шага,
 * список из двенадцати записей и решение, которое принимают не за десять секунд — иногда
 * владельцу надо позвонить мастеру и вернуться. Окно без адреса теряется при обновлении
 * страницы вместе со всем набранным, а ссылку на него нельзя переслать управляющему.
 *
 * ПОРЯДОК ШАГОВ — ЭТО СМЫСЛ, А НЕ ВЁРСТКА: период → что с записями → что сказать
 * клиентам → итог. Каждый следующий шаг зависит от предыдущего, и итог считается
 * по всем трём. Поэтому итог стоит ДО кнопки: решение принимают по нему.
 */

export function EmployeeLeaveScreen(props: {
  readonly employeeId: string
  readonly onClose: () => void
  /**
   * Период из просьбы мастера (5-М6): кнопка «Оформить отсутствие» в ленте открывает форму
   * уже с этими датами — администратору не нужно перепечатывать их из текста дела.
   */
  readonly requested?: { readonly from: string; readonly to: string } | null
}): ReactNode {
  const { context } = useCabinetAccess()
  const canReassign = useHasPermission("appointments.reschedule")
  const canManageShifts = useHasPermission("staff.schedule.manage")
  const canArchive = useHasPermission("staff.manage")

  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const [periodKind, setPeriodKind] = useState<LeavePeriodKind>("today")
  const requested = props.requested ?? null
  const [byRequest, setByRequest] = useState(requested !== null)
  const [action, setAction] = useState<LeaveAction>("reassign")
  const [notify, setNotify] = useState<LeaveNotify>("template")
  const [toEmployeeId, setToEmployeeId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [done, setDone] = useState<readonly string[] | null>(null)

  const employees = useResource((signal) => fetchEmployees(false, signal), "leave-employees")

  const period =
    byRequest && requested !== null
      ? { kind: "until_date" as const, dateFrom: requested.from < today ? today : requested.from, dateTo: requested.to }
      : resolvePeriod(periodKind, today)

  const journalQuery = useMemo(
    () =>
      branch === null
        ? null
        : {
            branchId: branch.id,
            dateFrom: period.dateFrom,
            dateTo: period.dateTo,
            includeCancelled: false,
          },
    [branch, period.dateFrom, period.dateTo],
  )

  const schedule = useResource(
    journalQuery === null ? null : (signal) => fetchJournal(journalQuery, signal),
    journalQuery === null ? null : `${journalQuery.branchId}|${period.dateFrom}|${period.dateTo}`,
  )

  const leaving = (employees.data ?? []).find((employee) => employee.id === props.employeeId) ?? null
  const target = (employees.data ?? []).find((employee) => employee.id === toEmployeeId) ?? null

  const candidates = (employees.data ?? []).filter(
    (employee) =>
      employee.id !== props.employeeId && employee.isBookable && employee.archivedAt === null,
  )

  const preview = useMemo(
    () =>
      schedule.data === null
        ? null
        : buildLeavePreview({
            result: schedule.data,
            fromEmployeeId: props.employeeId,
            toEmployeeId: action === "reassign" ? toEmployeeId : null,
            toEmployeeServiceIds: new Set(target?.serviceIds ?? []),
            period,
          }),
    [schedule.data, props.employeeId, action, toEmployeeId, target?.serviceIds, period],
  )

  const summary =
    preview === null
      ? null
      : buildLeaveSummary({
          preview,
          action,
          notify,
          toEmployeeName: target?.displayName ?? null,
          fromEmployeeName: leaving?.displayName ?? "мастер",
          periodKind: period.kind,
        })

  const apply = async (): Promise<void> => {
    if (branch === null || preview === null || summary === null) return
    setBusy(true)
    setProblem(null)
    try {
      const moved: string[] = []

      if (action === "reassign" || action === "unassign") {
        // По одной дате за вызов: у reassign-day параметр date — календарный день,
        // периода он не принимает. Частичный успех здесь норма, а не сбой: часть дней
        // может переехать, часть — упереться в занятость.
        // При передаче едут только те, что встают без изменений; при «без мастера» —
        // все: колонка «Без мастера» тем и хороша, что смену принимающего не спрашивает.
        const rows = action === "reassign" ? preview.fits : preview.all

        for (const date of datesOf(rows.map((row) => row.date))) {
          const ids = rows.filter((row) => row.date === date).map((row) => row.id)
          if (ids.length === 0) continue

          // Дни идут ПО ОЧЕРЕДИ намеренно, и правило «не ждать внутри цикла» здесь
          // выключено осознанно: параллельные переназначения гоняются за одно и то же
          // время у принимающего мастера, и часть из них вернулась бы конфликтом
          // занятости, которого в последовательном порядке просто не возникает.
          // eslint-disable-next-line no-await-in-loop
          const result = await reassignDay({
            branchId: branch.id,
            date,
            fromEmployeeId: props.employeeId,
            toEmployeeId: action === "reassign" ? toEmployeeId : null,
            appointmentIds: ids,
            notifyClients: notify !== "none",
            acknowledgedWarnings: [],
          })
          moved.push(...result.movedAppointmentIds)
        }
      }

      // Смены закрываются отсутствием — иначе на освободившееся время запишутся онлайн
      // ровно в тот момент, когда мастера в салоне нет.
      if (canManageShifts) {
        await createTimeOff({
          employeeId: props.employeeId,
          branchId: branch.id,
          dateFrom: period.dateFrom,
          dateTo: period.dateTo,
          existingAppointments: "keep",
        })
      }

      if (periodKind === "forever" && canArchive && leaving?.updatedAt !== null) {
        await archiveEmployee({
          employeeId: props.employeeId,
          reassignToEmployeeId: action === "reassign" ? toEmployeeId : null,
          notifyClients: notify !== "none",
          expectedUpdatedAt: leaving?.updatedAt ?? "",
        })
      }

      setDone(moved)
    } catch (error) {
      setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
    } finally {
      setBusy(false)
    }
  }

  if (employees.problem !== null && employees.data === null) {
    return <ProblemScreen problem={employees.problem} onRetry={employees.reload} />
  }

  if (done !== null) {
    return (
      <div className={styles.screen}>
        <StandHeader title="Готово" />
        <CenterState
          title={`Передано записей: ${done.length}`}
          text="Остальные остались в списке на разбор — их видно в журнале в колонке «Без мастера»."
          actions={
            <Button variant="primary" onClick={props.onClose}>
              Вернуться к сотрудникам
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className={styles.screen}>
      <StandHeader title={`${leaving?.displayName ?? "Мастер"} выбывает`} />

      <div className={styles.sheetScroll}>
        <div className={styles.sheet}>
          <p className={styles.rowSub}>
            Один сценарий на «заболела» и «уволилась»: период → что с записями → что сказать
            клиентам → итог списком
          </p>

          <div className={styles.steps}>
            {["1 · Период", "2 · Записи", "3 · Клиентам", "4 · Итог"].map((label, index) => (
              <div key={label} className={cx(styles.step, index <= 2 && styles.stepActive)}>
                {label}
              </div>
            ))}
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Период</span>
            <Chips label="Период отсутствия">
              {requested === null ? null : (
                <Chip
                  active={byRequest}
                  onClick={() => {
                    setByRequest(true)
                  }}
                >
                  По просьбе мастера: {requested.from === requested.to ? requested.from : `${requested.from} – ${requested.to}`}
                </Chip>
              )}
              {PERIODS.map((item) => (
                <Chip
                  key={item.kind}
                  active={!byRequest && periodKind === item.kind}
                  onClick={() => {
                    setByRequest(false)
                    setPeriodKind(item.kind)
                  }}
                >
                  {item.label}
                </Chip>
              ))}
            </Chips>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {preview === null
                ? "Что делать с записями"
                : `Что делать с ${appointmentsInstrumental(preview.all.length)}`}
            </span>

            {/*
              Настоящие радиокнопки, а не кнопки с подсветкой: выбор один из трёх,
              и стрелки клавиатуры обязаны по нему ходить. Кроме того, выпадающий список
              мастеров стоит ВНУТРИ варианта, а вложить <select> в <button> нельзя —
              браузер такую разметку разбирает не так, как она написана.
            */}
            <div role="radiogroup" aria-label="Что делать с записями" className={styles.field}>
              <label className={cx(styles.option, action === "reassign" && styles.optionActive)}>
                <span className={styles.optionHead}>
                  <span>
                    <input
                      type="radio"
                      name="leave-action"
                      checked={action === "reassign"}
                      onChange={() => {
                        setAction("reassign")
                      }}
                    />{" "}
                    <b>Передать другому мастеру</b>
                  </span>
                </span>
              </label>

              {action === "reassign" ? (
                <div className={styles.option}>
                  <span className={styles.optionHead}>
                    <span className={styles.fieldLabel}>Кому передаём</span>
                    <select
                      className={styles.input}
                      aria-label="Мастер, которому передаём записи"
                      value={toEmployeeId ?? ""}
                      onChange={(event) => {
                        setToEmployeeId(event.target.value.length === 0 ? null : event.target.value)
                      }}
                    >
                      <option value="">Выберите мастера</option>
                      {candidates.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.displayName}
                        </option>
                      ))}
                    </select>
                  </span>

                  {schedule.loading ? (
                    <Skeleton width="100%" height={34} radius={6} />
                  ) : preview === null || toEmployeeId === null ? (
                    <span className={styles.rowSub}>
                      Выберите мастера — посчитаем, сколько записей встанет к нему без изменений.
                    </span>
                  ) : (
                    <span className={styles.previewTiles}>
                      <span className={styles.tileOk}>
                        <b>{preview.fits.length}</b> встанут без изменений
                      </span>
                      <span className={styles.tileWarn}>
                        <b>{preview.outsideShift.length}</b> вне смены
                      </span>
                      <span className={styles.tileDanger}>
                        <b>{preview.serviceMissing.length}</b>{" "}
                        {preview.serviceMissing.length === 0
                          ? "услуг не выполняет"
                          : `услугу не выполняет — ${missingServiceNames(preview).join(", ")}`}
                      </span>
                    </span>
                  )}
                </div>
              ) : null}

              <label className={cx(styles.option, action === "unassign" && styles.optionActive)}>
                <span>
                  <input
                    type="radio"
                    name="leave-action"
                    checked={action === "unassign"}
                    onChange={() => {
                      setAction("unassign")
                    }}
                  />{" "}
                  <b>Оставить без мастера</b>{" "}
                  <span className={styles.rowSub}>
                    — записи уйдут в колонку «Без мастера», разберёте по одной. Честный вариант,
                    когда заменить некем.
                  </span>
                </span>
              </label>

              <label className={cx(styles.option, action === "cancel" && styles.optionActive)}>
                <span>
                  <input
                    type="radio"
                    name="leave-action"
                    checked={action === "cancel"}
                    onChange={() => {
                      setAction("cancel")
                    }}
                  />{" "}
                  <b>Отменить все</b>{" "}
                  <span className={styles.rowSub}>
                    — инициатор салон: клиентам не засчитывается, предоплаты не сгорают. Причина
                    обязательна.
                  </span>
                </span>
              </label>
            </div>

            {action === "cancel" ? (
              <p className={styles.warnBox}>
                Массовая отмена из этого экрана пока не выполняется: у переназначения нет
                режима «отменить», а отмена по одной записи идёт из журнала, где видно
                последствия для каждого клиента.
              </p>
            ) : null}
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Что сказать клиентам</span>
            <Chips label="Уведомление клиентов">
              {NOTIFY_OPTIONS.map((item) => (
                <Chip
                  key={item.kind}
                  active={notify === item.kind}
                  disabled={item.kind === "self_reschedule"}
                  onClick={() => {
                    setNotify(item.kind)
                  }}
                >
                  {item.label}
                </Chip>
              ))}
            </Chips>
            <small className={styles.rowSub}>{NOTIFY_TEMPLATE}</small>
            {notify === "self_reschedule" ? null : (
              <small className={styles.rowSub}>
                «Уведомить и дать перенести самим» пока недоступно: у переназначения нет
                такого способа уведомления — только «уведомлять» или «нет».
              </small>
            )}
          </div>

          {summary === null ? null : (
            <div className={styles.summaryBox}>
              <b>Итог до применения:</b> {summary.text}
            </div>
          )}

          {problem === null ? null : (
            <p className={styles.dangerBox} role="alert">
              {problem.title}. {problem.detail ?? "Часть записей могла уже переехать — откройте журнал и проверьте."}
            </p>
          )}

          <div className={styles.dialogActions}>
            <Button onClick={props.onClose}>Отмена</Button>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Применяем…"
              deniedReason={applyDeniedReason({
                canReassign,
                action,
                toEmployeeId,
                empty: preview === null,
              })}
              onClick={() => {
                void apply()
              }}
            >
              {summary?.applyLabel ?? "Применить"}
            </Button>
          </div>
        </div>
      </div>

      <p className={styles.rowSub}>
        Время считается по зоне филиала: сейчас там{" "}
        {formatTimeOfDay(new Date(), timeZone)}.
      </p>
    </div>
  )
}

const PERIODS: readonly { readonly kind: LeavePeriodKind; readonly label: string }[] = [
  { kind: "today", label: "Сегодня" },
  { kind: "until_friday", label: "До пятницы" },
  { kind: "until_date", label: "До даты…" },
  { kind: "forever", label: "Уволилась — навсегда" },
]

const NOTIFY_OPTIONS: readonly { readonly kind: LeaveNotify; readonly label: string }[] = [
  { kind: "none", label: "Не уведомлять — обзвоню сама" },
  { kind: "template", label: "Уведомить по шаблону" },
  { kind: "self_reschedule", label: "Уведомить и дать перенести самим" },
]

function applyDeniedReason(input: {
  readonly canReassign: boolean
  readonly action: LeaveAction
  readonly toEmployeeId: string | null
  readonly empty: boolean
}): string | undefined {
  if (!input.canReassign) return "Переносить чужие записи может администратор или управляющий"
  if (input.empty) return "Ждём, пока загрузятся записи за выбранный период"
  if (input.action === "reassign" && input.toEmployeeId === null) {
    return "Выберите мастера, которому передаём записи"
  }
  if (input.action === "cancel") {
    return "Массовая отмена делается из журнала — там видно последствия для каждого клиента"
  }
  return undefined
}

/** Даты по одной, без повторов и по порядку: переназначение работает подённо. */
function datesOf(values: readonly string[]): readonly string[] {
  return [...new Set(values)].toSorted()
}
